import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { AocDocumentWorkflow, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const INSPECTOR_ROLES = ['FLIGHT_OPS_INSPECTOR'];
const CATEGORIES = ['SCHEDULED_COMMERCIAL', 'CHARTER', 'AERIAL_WORK'];
const STATES = ['APPLICATION', 'DOCUMENT_COLLECTION', 'DEMO_FLIGHT', 'FEE_PENDING', 'CERTIFIED', 'REJECTED', 'SUSPENDED', 'REVOKED', 'EXPIRED', 'WITHDRAWN'];

/**
 * M23 — Air Operator Certificate document-heavy case workflow: a fixed
 * 7-document checklist (ops manual, maintenance programme, SMS manual, MEL,
 * insurance cert, crew licence register, emergency response plan), all
 * approved auto-advances to DEMO_FLIGHT, then fee + certification.
 */
export function AocCases() {
  const { persona } = useAuth();
  const isInspector = persona ? persona.roles.some((r) => INSPECTOR_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showOpen, setShowOpen] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m23/aoc-cases?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<AocDocumentWorkflow>>(path);

  return (
    <div>
      <PageHeader
        title="AOC Cases"
        subtitle="Air Operator Certificate — document checklist, demo flight, fee, certification (M23 — SCAA)."
        actions={
          <>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedQ(q);
              }}
            >
              <input className="field-input !w-40" placeholder="Case ref, operator…" value={q} onChange={(e) => setQ(e.target.value)} />
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All states</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-secondary">
                Search
              </button>
            </form>
            {isInspector ? (
              <button className="btn-primary" onClick={() => setShowOpen(true)}>
                + Open AOC case
              </button>
            ) : null}
          </>
        }
      />

      {!isInspector ? <RoleNotice roles={INSPECTOR_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No AOC cases match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Case ref</th>
                <th className="table-head">Operator</th>
                <th className="table-head">Category</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(c.id)}>
                  <td className="table-cell font-mono text-xs font-semibold">{c.caseRef}</td>
                  <td className="table-cell font-mono text-xs">{c.operatorId}</td>
                  <td className="table-cell text-xs text-slate-500">{c.operationCategory.replaceAll('_', ' ')}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showOpen ? (
        <OpenAocCaseModal
          onClose={() => setShowOpen(false)}
          onCreated={() => {
            setShowOpen(false);
            listQ.reload();
          }}
        />
      ) : null}

      {selectedId ? (
        <AocCaseDetailModal
          id={selectedId}
          isInspector={isInspector}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function OpenAocCaseModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [operatorId, setOperatorId] = useState('');
  const [operationCategory, setOperationCategory] = useState('SCHEDULED_COMMERCIAL');
  const [fleetDescription, setFleetDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m23/aoc-cases', {
        operatorType: 'ENTITY',
        operatorId: operatorId.trim(),
        operationCategory,
        fleetDescription: fleetDescription.trim(),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to open case');
    } finally {
      setBusy(false);
    }
  };

  const valid = operatorId.trim() && fleetDescription.trim().length >= 3;

  return (
    <Modal title="Open an AOC case" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <p className="mb-3 text-xs text-slate-500">ENTITY-only (an AOC holder is always a company) — seeds the 7-document checklist automatically.</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Operator business id" className="col-span-2">
          <input className="field-input" value={operatorId} onChange={(e) => setOperatorId(e.target.value)} />
        </Field>
        <Field label="Operation category">
          <select className="field-input" value={operationCategory} onChange={(e) => setOperationCategory(e.target.value)}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Fleet description" className="col-span-2">
          <textarea className="field-input" rows={2} value={fleetDescription} onChange={(e) => setFleetDescription(e.target.value)} placeholder="2x Cessna 208 Caravan, floatplane-equipped" />
        </Field>
      </div>
      <button className="btn-primary mt-4" disabled={busy || !valid} onClick={submit}>
        {busy ? 'Opening…' : 'Open case'}
      </button>
    </Modal>
  );
}

function AocCaseDetailModal({ id, isInspector, onClose, onChanged }: { id: string; isInspector: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<AocDocumentWorkflow>(`/api/v1/m23/aoc-cases/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [demoOutcome, setDemoOutcome] = useState<'PASS' | 'FAIL'>('PASS');
  const [demoReport, setDemoReport] = useState('');
  const [showAction, setShowAction] = useState<string | null>(null);

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const c = q.data;
  const approvedCount = c?.documents?.filter((d) => d.status === 'APPROVED').length ?? 0;
  const totalCount = c?.documents?.length ?? 0;

  return (
    <Modal title={c ? `AOC case ${c.caseRef}` : 'AOC case'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {c ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="State" value={<StatusBadge status={c.state} />} />
            <Detail label="Category" value={c.operationCategory.replaceAll('_', ' ')} />
            <Detail label="Documents approved" value={`${approvedCount} / ${totalCount}`} />
          </div>
          <Detail label="Fleet" value={c.fleetDescription} />
          {c.rejectionReason ? <ErrorNote message={`Rejected: ${c.rejectionReason}`} /> : null}
          {c.suspendedReason ? <ErrorNote message={`Suspended: ${c.suspendedReason}`} /> : null}

          {c.state === 'DOCUMENT_COLLECTION' && c.documents && c.documents.length > 0 ? (
            <div>
              <div className="field-label">Document checklist</div>
              <div className="space-y-1.5">
                {c.documents.map((d) => (
                  <div key={d.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-1.5 text-sm">
                    <span>{d.label}</span>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={d.status} />
                      {(d.status === 'PENDING' || d.status === 'REJECTED') ? (
                        <button
                          className="btn-secondary !py-1 !text-xs"
                          disabled={busy}
                          onClick={() => {
                            const ref = window.prompt(`Document ref for ${d.label}?`);
                            if (ref) call(() => api.post(`/api/v1/m23/aoc-cases/${c.id}/documents/${d.code}/submit`, { docRef: ref, sha256: '0'.repeat(64) }));
                          }}
                        >
                          Submit
                        </button>
                      ) : null}
                      {isInspector && d.status === 'SUBMITTED' ? (
                        <>
                          <button
                            className="btn-success !py-1 !text-xs"
                            disabled={busy}
                            onClick={() => call(() => api.post(`/api/v1/m23/aoc-cases/${c.id}/documents/${d.code}/review`, { decision: 'APPROVE' }))}
                          >
                            Approve
                          </button>
                          <button
                            className="btn-danger !py-1 !text-xs"
                            disabled={busy}
                            onClick={() => {
                              const note = window.prompt('Rejection note?') ?? undefined;
                              call(() => api.post(`/api/v1/m23/aoc-cases/${c.id}/documents/${d.code}/review`, { decision: 'REJECT', note }));
                            }}
                          >
                            Reject
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-1 text-xs text-slate-400">All documents APPROVED auto-advances the case to DEMO_FLIGHT.</p>
            </div>
          ) : null}

          {isInspector ? (
            <div className="flex flex-wrap gap-2">
              {c.state === 'DEMO_FLIGHT' ? (
                !c.demoFlightScheduledAt ? (
                  showAction === 'schedule-demo' ? (
                    <div className="flex items-end gap-2">
                      <Field label="Scheduled at">
                        <input className="field-input" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
                      </Field>
                      <button className="btn-primary" disabled={busy || !scheduledAt} onClick={() => call(() => api.post(`/api/v1/m23/aoc-cases/${c.id}/demo-flight/schedule`, { scheduledAt: new Date(scheduledAt).toISOString() }))}>
                        Confirm
                      </button>
                    </div>
                  ) : (
                    <button className="btn-primary" onClick={() => setShowAction('schedule-demo')}>
                      Schedule demo flight
                    </button>
                  )
                ) : showAction === 'demo-report' ? (
                  <div className="w-full space-y-2 rounded-lg border border-slate-200 p-3">
                    <div className="text-xs text-slate-500">Scheduled {formatDateTime(c.demoFlightScheduledAt)}</div>
                    <div className="flex gap-2">
                      <button className={`btn-success ${demoOutcome === 'PASS' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDemoOutcome('PASS')}>
                        Pass
                      </button>
                      <button className={`btn-danger ${demoOutcome === 'FAIL' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDemoOutcome('FAIL')}>
                        Fail
                      </button>
                    </div>
                    <Field label="Report">
                      <textarea className="field-input" rows={2} value={demoReport} onChange={(e) => setDemoReport(e.target.value)} />
                    </Field>
                    <button
                      className={demoOutcome === 'PASS' ? 'btn-success' : 'btn-danger'}
                      disabled={busy || demoReport.trim().length < 3}
                      onClick={() => call(() => api.post(`/api/v1/m23/aoc-cases/${c.id}/demo-flight/report`, { outcome: demoOutcome, report: demoReport.trim() }))}
                    >
                      {busy ? 'Submitting…' : `Confirm ${demoOutcome.toLowerCase()}`}
                    </button>
                  </div>
                ) : (
                  <button className="btn-secondary" onClick={() => setShowAction('demo-report')}>
                    Submit demo flight report
                  </button>
                )
              ) : null}

              {c.state === 'CERTIFIED' ? (
                showAction === 'suspend' ? (
                  <div className="flex items-end gap-2">
                    <Field label="Reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={() => call(() => api.post(`/api/v1/m23/aoc-cases/${c.id}/suspend`, { reason: reason.trim() }))}>
                      Confirm
                    </button>
                  </div>
                ) : (
                  <button className="btn-danger" onClick={() => setShowAction('suspend')}>
                    Suspend
                  </button>
                )
              ) : null}

              {c.state === 'SUSPENDED' ? (
                <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m23/aoc-cases/${c.id}/reinstate`))}>
                  Reinstate
                </button>
              ) : null}

              {c.state === 'CERTIFIED' || c.state === 'SUSPENDED' ? (
                showAction === 'revoke' ? (
                  <div className="flex items-end gap-2">
                    <Field label="Reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={() => call(() => api.post(`/api/v1/m23/aoc-cases/${c.id}/revoke`, { reason: reason.trim() }))}>
                      Confirm
                    </button>
                  </div>
                ) : (
                  <button className="btn-danger" onClick={() => setShowAction('revoke')}>
                    Revoke
                  </button>
                )
              ) : null}

              {!['CERTIFIED', 'REJECTED', 'REVOKED', 'WITHDRAWN', 'EXPIRED'].includes(c.state) ? (
                showAction === 'reject-case' ? (
                  <div className="flex items-end gap-2">
                    <Field label="Reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={() => call(() => api.post(`/api/v1/m23/aoc-cases/${c.id}/reject`, { reason: reason.trim() }))}>
                      Confirm
                    </button>
                  </div>
                ) : (
                  <button className="btn-danger" onClick={() => setShowAction('reject-case')}>
                    Reject whole case
                  </button>
                )
              ) : null}
            </div>
          ) : (
            <RoleNotice roles={INSPECTOR_ROLES} />
          )}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">Filed {formatDateTime(c.createdAt)}</div>
        </div>
      ) : null}
    </Modal>
  );
}
